import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { canViewAllDashboardAlerts } from "@/lib/roleUtils";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { RoleDashboard } from "@/components/Dashboard/RoleDashboard";
import { MoldMaintenanceAlerts } from "@/components/Dashboard/MoldMaintenanceAlerts";
import { LowStockAlerts } from "@/components/Dashboard/LowStockAlerts";
import { MetricCard } from "@/components/Dashboard/MetricCard";
import { AlertCard } from "@/components/Dashboard/AlertCard";
import { Package, Activity, Users, TrendingUp, RefreshCw, Filter } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRealtimeMetrics } from "@/hooks/useRealtimeMetrics";
import { useMetricsHistory } from "@/hooks/useMetricsHistory";
import { useKPIMonitoring } from "@/hooks/useKPIMonitoring";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const Index = () => {
  const { userRole } = useAuth();
  const { t } = useLanguage();
  const [areaFilter, setAreaFilter] = useState<string>("all");
  const { metrics, loading, refresh } = useRealtimeMetrics(areaFilter);
  const { history, loading: historyLoading } = useMetricsHistory(areaFilter);
  useKPIMonitoring(metrics);
  
  return (
    <DashboardLayout>
      <RoleDashboard>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">{t.dashboard.welcome}</h2>
            <p className="text-muted-foreground mt-1">
              {t.dashboard.subtitle}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={areaFilter} onValueChange={setAreaFilter}>
              <SelectTrigger className="w-[200px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder={t.filters.selectArea} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.filters.allAreas}</SelectItem>
                <SelectItem value="corte_plotter">{t.auth.areas.corte_plotter}</SelectItem>
                <SelectItem value="laminador">{t.auth.areas.laminador}</SelectItem>
                <SelectItem value="taller">{t.auth.areas.taller}</SelectItem>
                <SelectItem value="generico">{t.auth.areas.generico}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              {t.dashboard?.refresh || "Actualizar"}
            </Button>
          </div>
        </div>

        <MoldMaintenanceAlerts />

        <LowStockAlerts />

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-[140px] rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title={t.dashboard?.activeOrders || "Órdenes Activas"}
              value={metrics.activeOrders}
              change={metrics.activeOrders > 0 ? `${t.dashboard?.inProgress || "En progreso"}` : `${t.dashboard?.noActive || "Sin órdenes"}`}
              trend={metrics.activeOrders > 0 ? "up" : "neutral"}
              icon={Package}
              iconColor="text-primary"
            />
            <MetricCard
              title={t.dashboard?.inProgressProcesses || "Procesos en Curso"}
              value={metrics.inProgressProcesses}
              change={metrics.inProgressProcesses > 0 ? `${t.dashboard?.active || "Activos"}` : `${t.dashboard?.noActive || "Sin procesos"}`}
              trend={metrics.inProgressProcesses > 0 ? "up" : "neutral"}
              icon={Activity}
              iconColor="text-accent"
            />
            <MetricCard
              title={t.dashboard?.activeOperators || "Operarios Trabajando"}
              value={metrics.activeOperators}
              change={metrics.activeOperators > 0 ? `${t.dashboard?.workingNow || "Trabajando ahora"}` : `${t.dashboard?.noActive || "Sin operarios"}`}
              trend={metrics.activeOperators > 0 ? "up" : "neutral"}
              icon={Users}
              iconColor="text-chart-3"
            />
            <MetricCard
              title={t.dashboard?.completionRate || "Tasa de Finalización"}
              value={`${metrics.completionRate}%`}
              change={`${t.dashboard?.today || "Hoy"}`}
              trend={metrics.completionRate >= 80 ? "up" : metrics.completionRate >= 50 ? "neutral" : "down"}
              icon={TrendingUp}
              iconColor="text-accent"
            />
          </div>
        )}

        {!loading && canViewAllDashboardAlerts(userRole) && <AlertCard alerts={metrics.alerts} />}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t.dashboard.realtimeEvolution || "Evolución en Tiempo Real"}</CardTitle>
            </CardHeader>
            <CardContent>
              {historyLoading || history.length === 0 ? (
                <Skeleton className="h-[300px] w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={history}>
                    <defs>
                      <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorProcesses" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="time" className="text-xs text-muted-foreground" />
                    <YAxis className="text-xs text-muted-foreground" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="activeOrders" 
                      stroke="hsl(var(--primary))" 
                      fillOpacity={1} 
                      fill="url(#colorOrders)"
                      name={t.dashboard.activeOrders}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="inProgressProcesses" 
                      stroke="hsl(var(--accent))" 
                      fillOpacity={1} 
                      fill="url(#colorProcesses)"
                      name={t.dashboard.inProgressProcesses}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t.dashboard.performanceMetrics || "Métricas de Rendimiento"}</CardTitle>
            </CardHeader>
            <CardContent>
              {historyLoading || history.length === 0 ? (
                <Skeleton className="h-[300px] w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={history}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="time" className="text-xs text-muted-foreground" />
                    <YAxis className="text-xs text-muted-foreground" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="activeOperators" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--primary))' }}
                      name={t.dashboard.activeOperators}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="completionRate" 
                      stroke="hsl(var(--accent))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--accent))' }}
                      name={t.dashboard.completionRate}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </RoleDashboard>
    </DashboardLayout>
  );
};

export default Index;
